import { createPortal } from "react-dom";
import { useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Workbook } from "@fortune-sheet/react";
import "@fortune-sheet/react/dist/index.css";
import { X, DownloadSimple } from "@phosphor-icons/react";

/**
 * Google-Sheets-style analysis modal (add-on — never replaces the source table).
 * Loads a SNAPSHOT of the given rows into a real spreadsheet grid: drag-select any range
 * of cells and the bottom status bar shows Count / Sum / Average / Min / Max, and you can
 * type your own formulas (=SUM(...) etc.) or export. Edits here do NOT write back to source.
 *
 * Props:
 *   title    — heading shown in the modal bar
 *   columns  — [{ header, value:(row)=>any, numeric?:bool, width?:number }]
 *   rows     — the (already filtered) rows to load
 *   onClose  — () => void
 */
export function SpreadsheetModal({ title = "Spreadsheet", columns, rows, onClose }) {
  const sheets = useMemo(() => {
    const celldata = [];
    // Header row (dark, bold)
    columns.forEach((col, c) => {
      celldata.push({ r: 0, c, v: { v: col.header, m: col.header, bl: 1, bg: "#1E293B", fc: "#FFFFFF", ht: 0 } });
    });
    rows.forEach((row, i) => {
      const r = i + 1;
      columns.forEach((col, c) => {
        const raw = col.value(row);
        // xlsxDate() (reused from the shared export columns) returns a marker with an Excel serial
        // — store a REAL date cell so the spreadsheet sorts chronologically (not as text), while
        // still displaying the friendly dd-mmm-yy format.
        if (raw && typeof raw === "object" && raw.__xlsxDate) {
          if (raw.serial != null) {
            celldata.push({ r, c, v: { v: raw.serial, m: raw.display || "", ct: { fa: "dd-mmm-yy", t: "d" } } });
          } else {
            const s = raw.display || "";
            celldata.push({ r, c, v: { v: s, m: s, ct: { fa: "General", t: "g" } } });
          }
        } else if (col.numeric) {
          const nv = Number(raw) || 0;
          // Monitor string must be a PLAIN number (no locale commas) — Fortune-Sheet parses `m`
          // for its status-bar Sum/Avg, and "2,520" would parseFloat to 2. Grouping for DISPLAY
          // comes from the cell format (fa) instead.
          celldata.push({ r, c, v: { v: nv, m: String(nv), ct: { fa: "#,##0", t: "n" } } });
        } else {
          const s = raw == null ? "" : String(raw);
          celldata.push({ r, c, v: { v: s, m: s, ct: { fa: "General", t: "g" } } });
        }
      });
    });
    const columnlen = {};
    columns.forEach((col, c) => { columnlen[c] = col.width || (col.numeric ? 100 : 150); });
    return [{
      name: title.slice(0, 28) || "Data",
      celldata,
      config: { columnlen, rowlen: { 0: 26 } },
      row: rows.length + 50,
      column: columns.length + 4,
      frozen: { type: "row", range: { row_focus: 0, column_focus: 0 } },
    }];
  }, [columns, rows, title]);

  const wbRef = useRef(null);

  // Mobile-only totals helper (drag-select is unreliable on touch canvases). Pick a numeric column
  // and see Count/Sum/Average/Min/Max across all loaded (filtered) rows. Hidden on desktop via CSS.
  const numericCols = useMemo(
    () => columns.map((c, i) => ({ ...c, _i: i })).filter((c) => c.numeric),
    [columns]
  );
  const [statCol, setStatCol] = useState(0);
  const stat = useMemo(() => {
    const col = numericCols[statCol] || numericCols[0];
    if (!col) return null;
    const vals = [];
    rows.forEach((r) => {
      const raw = col.value(r);
      const n = Number(raw);
      if (raw !== "" && raw != null && !Number.isNaN(n)) vals.push(n);
    });
    if (!vals.length) return { header: col.header, count: 0, sum: 0, avg: 0, min: 0, max: 0 };
    const sum = vals.reduce((a, b) => a + b, 0);
    return { header: col.header, count: vals.length, sum, avg: sum / vals.length, min: Math.min(...vals), max: Math.max(...vals) };
  }, [numericCols, statCol, rows]);
  const nf = (n) => Math.round(n).toLocaleString("en-IN");

  // Save the CURRENT workbook (including any formulas / edits the user added) to a real .xlsx.
  const saveXlsx = () => {
    const all = wbRef.current?.getAllSheets?.() || [];
    if (!all.length) return;
    const wb = XLSX.utils.book_new();
    all.forEach((sh, idx) => {
      // getAllSheets() returns the live `data` matrix; fall back to celldata if needed.
      let matrix = sh.data;
      if (!matrix && sh.celldata) {
        const maxR = Math.max(0, ...sh.celldata.map((c) => c.r));
        const maxC = Math.max(0, ...sh.celldata.map((c) => c.c));
        matrix = Array.from({ length: maxR + 1 }, () => Array(maxC + 1).fill(null));
        sh.celldata.forEach((c) => { matrix[c.r][c.c] = c.v; });
      }
      matrix = matrix || [];
      const ws = {};
      let maxR = 0, maxC = 0;
      matrix.forEach((row, r) => {
        if (!row) return;
        row.forEach((cell, c) => {
          if (cell == null) return;
          const isNum = cell.ct && cell.ct.t === "n";
          const isDate = cell.ct && cell.ct.t === "d";
          let out;
          if (cell.f) {
            // Preserve the user's formula (SheetJS wants it without the leading "=").
            out = { t: isNum ? "n" : "s", f: String(cell.f).replace(/^=/, "") };
            if (cell.v != null && cell.v !== "") out.v = isNum ? Number(cell.v) : cell.v;
          } else if (isDate) {
            // Excel serial + date format => a real, sortable date in the saved workbook.
            out = { t: "n", v: Number(cell.v) || 0, z: "dd-mmm-yy" };
          } else if (isNum) {
            out = { t: "n", v: Number(cell.v) || 0 };
          } else {
            const s = cell.m != null ? cell.m : (cell.v != null ? cell.v : "");
            out = { t: "s", v: String(s) };
          }
          ws[XLSX.utils.encode_cell({ r, c })] = out;
          if (r > maxR) maxR = r;
          if (c > maxC) maxC = c;
        });
      });
      ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: maxR, c: maxC } });
      XLSX.utils.book_append_sheet(wb, ws, String(sh.name || `Sheet${idx + 1}`).slice(0, 31));
    });
    const d = new Date();
    const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const base = title.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "spreadsheet";
    XLSX.writeFile(wb, `${base}-${stamp}.xlsx`);
  };

  return createPortal(
    <div data-testid="spreadsheet-modal" style={{
      position: "fixed", inset: 0, zIndex: 9999, background: "rgba(15,23,42,0.55)",
      display: "flex", flexDirection: "column", padding: 18,
    }}>
      <div style={{
        flex: 1, minHeight: 0, background: "#fff", borderRadius: 10, overflow: "hidden",
        display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
          borderBottom: "1px solid #E2E8F0", background: "#F8FAFC", flex: "0 0 auto",
        }}>
          <span style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.3, color: "#0F172A" }}>{title.toUpperCase()}</span>
          <span className="ss-subtitle" style={{ fontSize: 11.5, color: "#64748B" }}>
            {rows.length.toLocaleString("en-IN")} rows · drag-select any range to see totals at the bottom · this is an analysis snapshot (edits don't affect your data)
          </span>
          <div style={{ flex: 1 }} />
          <button data-testid="spreadsheet-save-xlsx" onClick={saveXlsx}
            className="jde-btn jde-btn-sm jde-btn-primary"
            title="Save this spreadsheet (including any formulas you added) to an Excel .xlsx file"
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <DownloadSimple size={13} weight="bold" /> Save to .xlsx
          </button>
          <button data-testid="spreadsheet-close" onClick={onClose}
            className="jde-btn jde-btn-sm"
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <X size={13} weight="bold" /> Close
          </button>
        </div>
        {numericCols.length > 0 && stat && (
          <>
            <style>{`
              .ss-mobile-totals { display: none; }
              @media (max-width: 768px) {
                .ss-mobile-totals { display: flex; }
              }
            `}</style>
            <div className="ss-mobile-totals" data-testid="ss-mobile-totals" style={{
              alignItems: "center", gap: 8, padding: "8px 12px", flex: "0 0 auto",
              borderBottom: "1px solid #1E293B", background: "#0F172A", color: "#E2E8F0",
              overflowX: "auto", whiteSpace: "nowrap",
            }}>
              <select data-testid="ss-mobile-col" value={statCol} onChange={(e) => setStatCol(Number(e.target.value))}
                style={{ flex: "0 0 auto", height: 30, borderRadius: 6, border: "1px solid #334155", background: "#1E293B", color: "#F1F5F9", fontSize: 12.5, padding: "0 8px" }}>
                {numericCols.map((c, idx) => <option key={idx} value={idx}>{c.header}</option>)}
              </select>
              <MStat label="Count" value={stat.count.toLocaleString("en-IN")} />
              <MStat label="Sum" value={nf(stat.sum)} strong />
              <MStat label="Avg" value={nf(stat.avg)} />
              <MStat label="Min" value={nf(stat.min)} />
              <MStat label="Max" value={nf(stat.max)} />
            </div>
          </>
        )}
        <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
          <Workbook ref={wbRef} data={sheets} showToolbar showSheetTabs={false} allowEdit lang="en" />
        </div>
      </div>
    </div>,
    document.body
  );
}

const MStat = ({ label, value, strong }) => (
  <div style={{ flex: "0 0 auto", padding: "2px 10px", borderLeft: "1px solid #1E293B" }}>
    <div style={{ fontSize: 9.5, letterSpacing: 0.4, textTransform: "uppercase", color: strong ? "#60A5FA" : "#94A3B8" }}>{label}</div>
    <div style={{ fontSize: strong ? 15 : 13.5, fontWeight: strong ? 800 : 700, color: strong ? "#93C5FD" : "#F1F5F9" }}>{value}</div>
  </div>
);
